import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, X } from "lucide-react";
import { Accounts } from "@/api/endpoints";

interface Props {
  value: string | null;
  onChange: (accountId: string | null) => void;
  disabled?: boolean;
  /** When known, the currently-selected account name (e.g. from deal.account_name)
   *  so we can render it without an extra fetch. */
  currentName?: string | null;
}

/**
 * Typeahead over Accounts.list({ q }). Lets the user search by name and pick an
 * account, or clear to "Unassigned" (null). When `disabled`, renders a read-only
 * label of the current account name (mirrors DealInfoPanel's view-only treatment).
 */
export default function AccountPicker({ value, onChange, disabled, currentName }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounce the search term so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  const listQuery = useQuery({
    queryKey: ["accounts", debounced],
    queryFn: () => Accounts.list({ q: debounced || undefined }),
    enabled: open && !disabled,
  });

  // Resolve the selected account's name. Prefer whatever the list query has
  // loaded (always reflects the live `value`); fall back to the caller-provided
  // name (e.g. before the list has loaded). When value is null, no name.
  const selectedName =
    value === null
      ? null
      : ((listQuery.data ?? []).find((a) => a.id === value)?.name ?? currentName ?? null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (disabled) {
    return (
      <div className="input flex items-center opacity-60 cursor-not-allowed">
        {selectedName ?? <span className="text-mut-500">Unassigned</span>}
      </div>
    );
  }

  const pick = (id: string | null) => {
    onChange(id);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className="input flex items-center justify-between gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selectedName ? "" : "text-mut-500"}>
          {selectedName ?? "Unassigned"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-mut-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full panel p-1.5 max-h-72 overflow-y-auto">
          <input
            autoFocus
            className="input mb-1.5"
            placeholder="Search accounts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <button
            type="button"
            className="cmdk-item w-full"
            data-selected={value === null ? "true" : "false"}
            onClick={() => pick(null)}
          >
            <X className="w-[15px] h-[15px] text-mut-300" />
            <span className="flex-1">Unassigned</span>
            {value === null && <Check className="w-3.5 h-3.5 text-accent-azure" />}
          </button>

          {listQuery.isLoading ? (
            <div className="px-3 py-2 text-sm text-mut-300">Loading…</div>
          ) : (
            (listQuery.data ?? []).map((a) => (
              <button
                key={a.id}
                type="button"
                className="cmdk-item w-full"
                data-selected={value === a.id ? "true" : "false"}
                onClick={() => pick(a.id)}
              >
                <span className="truncate flex-1">{a.name}</span>
                <span className="meta">{a.deal_count} deals</span>
                {value === a.id && <Check className="w-3.5 h-3.5 text-accent-azure" />}
              </button>
            ))
          )}

          {!listQuery.isLoading && (listQuery.data ?? []).length === 0 && (
            <div className="px-3 py-2 text-sm text-mut-300">No accounts found.</div>
          )}
        </div>
      )}
    </div>
  );
}
